/**
 * Adding a site.
 *
 * The dialog was hand-rolled: its own backdrop, its own close button, a circle
 * with a globe in it beside the title, and a raw <input>. None of that was
 * carrying information. The circle in particular is the pattern where an icon
 * repeats the heading it sits next to, at the size of a thing you could press.
 *
 * It also opened nothing when you pressed Escape, trapped no focus, and let
 * the page behind it scroll. The design system's Dialog does all three.
 *
 * It still normalizes what you type (protocol, www, path, port) before it
 * sends it, and now the hint says so in words rather than in the word
 * "normalize".
 */

import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Plus } from 'lucide-react'
import { DsIsland } from '@/design-system/island'
import { Button, Dialog, Field, Input } from '@/design-system/primitives'
import { apiService } from '@/services/api'
import { useDomain } from '@/hooks/useDomain'
import { useTeam } from '@/hooks/useTeam'
import DomainLimitModal from '@/components/DomainLimitModal'
import type { UnifiedDomain } from '@shared/types'

const FORM_ID = 'add-a-site'

interface AddDomainDialogProps {
  isOpen: boolean
  onClose: () => void
  /** If true, auto-select the newly created domain in the global context */
  autoSelect?: boolean
}

/**
 * Normalize a user-typed domain string:
 * - Strip protocol (http://, https://)
 * - Strip www. prefix
 * - Strip trailing slashes and paths
 * - Lowercase
 */
function normalizeDomainInput(input: string): string {
  let domain = input.trim().toLowerCase()

  // Strip protocol
  domain = domain.replace(/^https?:\/\//, '')

  // Strip www.
  domain = domain.replace(/^www\./, '')

  // Strip path / trailing slash
  domain = domain.split('/')[0]

  // Strip port
  domain = domain.split(':')[0]

  return domain
}

/** Basic domain format validation */
function isValidDomain(domain: string): boolean {
  // Must have at least one dot and only valid chars
  return /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/.test(domain)
}

export default function AddDomainDialog({ isOpen, onClose, autoSelect = true }: AddDomainDialogProps) {
  const [domainInput, setDomainInput] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [showLimitModal, setShowLimitModal] = useState(false)
  const queryClient = useQueryClient()
  const { setSelectedDomain } = useDomain()
  const { currentTeam } = useTeam()
  const teamId = currentTeam?.team?.id

  const { data: limitsData } = useQuery({
    queryKey: ['domain-limits', teamId],
    queryFn: () => apiService.getDomainLimits(),
    enabled: !!teamId,
  })

  const createMutation = useMutation({
    mutationFn: (domain: string) => apiService.createUnifiedDomain(domain),
    onSuccess: (response) => {
      const newDomain = response.data as UnifiedDomain
      // Invalidate domains and limits queries so the list and counter refresh
      queryClient.invalidateQueries({ queryKey: ['unified-domains', teamId] })
      queryClient.invalidateQueries({ queryKey: ['domain-limits', teamId] })
      // The chief briefing must pick up the new domain immediately (setting-up
      // row, activation checklist): invalidate every chief cache, same idiom
      // useTeam uses on team switches.
      queryClient.invalidateQueries({ predicate: (q) => String(q.queryKey[0]).startsWith('chief-') })

      if (autoSelect && newDomain) {
        setSelectedDomain(newDomain)
      }

      // Reset and close
      setDomainInput('')
      setError(null)
      onClose()
    },
    onError: (err: Error & { response?: { data?: { error?: string }; status?: number } }) => {
      const message = err.response?.data?.error || err.message || 'The site could not be added.'
      if (message.toLowerCase().includes('domain limit')) {
        setShowLimitModal(true)
      } else {
        setError(message)
      }
    }
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    const normalized = normalizeDomainInput(domainInput)

    if (!normalized) {
      setError('Enter the address of the site.')
      return
    }

    if (!isValidDomain(normalized)) {
      setError('That does not look like a web address. Try something like example.com.')
      return
    }

    createMutation.mutate(normalized)
  }

  const handleClose = () => {
    if (createMutation.isPending) return
    setDomainInput('')
    setError(null)
    onClose()
  }

  return (
    <>
      {/* The scope has to be here rather than assumed from the page: this
          dialog opens from the dashboard, the sites card and DomainsPage, and
          only two of those three are converted. */}
      <DsIsland panel={false} ground={false}>
      <Dialog
        open={isOpen}
        onClose={handleClose}
        title="Add a site"
        lead="Everything you generate and monitor is filed under the site it belongs to."
        size="md"
        actions={
          <>
            <Button variant="secondary" onClick={handleClose} disabled={createMutation.isPending}>
              Cancel
            </Button>
            <Button
              type="submit"
              form={FORM_ID}
              loading={createMutation.isPending}
              disabled={!domainInput.trim()}
            >
              <Plus aria-hidden="true" />
              Add site
            </Button>
          </>
        }
      >
        {/*
          The submit button lives in the dialog's action row, which is outside
          this form in the DOM. `form` on the button is what still submits it,
          so Enter in the field and a press of the button do the same thing.
        */}
        <form id={FORM_ID} onSubmit={handleSubmit}>
          <Field
            label="Website address"
            hint="Paste the whole address if that is easier. We only keep the domain."
            error={error ?? undefined}
          >
            {(id, describedBy) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                type="text"
                value={domainInput}
                onChange={(e) => {
                  setDomainInput(e.target.value)
                  if (error) setError(null)
                }}
                placeholder="example.com"
                autoFocus
                disabled={createMutation.isPending}
              />
            )}
          </Field>
        </form>
      </Dialog>
      </DsIsland>

      {/* Domain Limit Upgrade Modal */}
      <DomainLimitModal
        isOpen={showLimitModal}
        onClose={() => setShowLimitModal(false)}
        currentCount={limitsData?.data?.current ?? 1}
        maxCount={limitsData?.data?.max ?? 1}
        reason="add another site"
        triggerSource="AddDomainDialog"
      />
    </>
  )
}
